// VRG-733 — GTM → Articles → Cover. API client for `/internal/gtm/covers/*`.
//
// Types declared locally, like gtmApi.ts. Canonical definitions:
// `apps/api/src/features/gtm/covers/article-covers.service.ts` (the *View helpers). Keep in step.

import { createApiCall } from './index'

export type CoverMode = 'mock' | 'live'

export interface CoverEstimate {
  lo: number
  hi: number
  ceiling?: number
  label: string
}

export interface CoverConcept {
  id: string
  idea: string
  claim_quote: string
  claim_why?: string
  scene_type: string
  camera?: string
  scale?: string
  primary_prop?: string | null
  props?: string[]
  tag_visibility: string
  tag_placement?: string
  violet_accent?: string
  trade_dress_risks?: string[]
  subject_override?: string | null
  /** rules v2: subject | in-scene | absent (missing on v1 sets = subject) */
  object_role?: string | null
  metaphor: string
  alt: string
  variation?: string
}

export interface CoverConceptSet {
  id: string
  runId: string
  createdAt: string
  valid: boolean
  problems: string[]
  source: { kind: 'website' | 'pasted'; label?: string; ref?: string; sha: string }
  draft: { brief?: string; object: string; objectRef?: string | null; tagMark: string; forbiddenFeatures: string[]; concepts: CoverConcept[] }
  prompts: Record<string, string | null>
}

export interface CoverQaCheck {
  key: 'text' | 'realism' | 'title' | 'backdrop' | 'tag' | 'size'
  label: string
  ok: boolean
  detail: string
}

export interface CoverImage {
  id: string
  runId: string
  variant: number
  conceptSetId: string | null
  concept: CoverConcept & { object?: string }
  promptSource: 'concept' | 'edited' | 'custom'
  promptSha: string
  stored: boolean
  width: number | null
  height: number | null
  bytes: number | null
  thoughtPreviews: number
  error: string | null
  estimatedUsd: number
  measuredUsd: number | null
  qaUsd: number | null
  qa: { score: number | null; hardFail: string | null; checks: CoverQaCheck[]; flag: string | null; notes: string | null } | null
}

export interface CoverRun {
  id: string
  section: string
  slug: string
  kind: 'concepts' | 'tests' | 'approve'
  mode: CoverMode
  model: string
  settings: Record<string, unknown>
  input: Record<string, unknown>
  requestedBy: string | null
  status: 'running' | 'succeeded' | 'failed' | 'refused'
  estimatedUsd: number
  measuredUsd: number | null
  outcome: string | null
  error: string | null
  startedAt: string
  finishedAt: string | null
}

export interface CoverPr {
  status: 'opened' | 'mock' | 'failed' | 'written'
  url: string | null
  number: number | null
  branch: string | null
  files: string[]
  error: string | null
  at?: string
  body?: string
}

export interface CoverApproval {
  section: string
  slug: string
  imageId: string
  mode: CoverMode
  alt: string
  files: { master: { key: string; width: number; height: number; bytes: number }; webp: { key: string; width: number; height: number; bytes: number }; website: { path: string; src: string; published: boolean } }
  pr: CoverPr | null
  approvedBy: string | null
  approvedAt: string
}

export interface CoverCap {
  capUsd: number
  spentUsd: number
  remainingUsd: number
  month: string
  mode: CoverMode
  updatedBy: string | null
}

export interface CoverConfigView {
  mode: CoverMode
  ready: boolean
  missing: string[]
  /** apply (approve + website PR) can run; generation may be ready without it */
  applyReady?: boolean
  applyMissing?: string[]
  /** 'local': approve writes into the local website checkout instead of opening a PR (2026-10-29). */
  apply?: 'pr' | 'local'
  /** Imagen 2x upscale offered at approve; usd is an unverified estimate. */
  upscale?: { model: string; factor: string; usd: number; usdHi: number }
  articleSource?: string
  store: string
  website: string
  websiteBase: string
  vertexProject: string | null
  vertexLocation: string
  models: { image: string; concept: string; qa: string }
  imageSize: string
  aspectRatio: string
  canon: { commit: string | null; ref: string; syncedOn: string; rulesVersion?: number }
}

export interface CoverState {
  config: CoverConfigView
  cap: CoverCap
  article: { available: true; source: string; ref: string; words: number; sha: string } | { available: false; reason: string }
  pin: { class: string; by: string; optional?: boolean } | null
  estimates: { concepts: CoverEstimate | null; tests: Record<string, CoverEstimate | null>; sample?: Record<string, CoverEstimate | null> }
  conceptSets: CoverConceptSet[]
  tests: { run: CoverRun; images: CoverImage[] } | null
  approval: CoverApproval | null
  runs: CoverRun[]
}

export type ArticleCommentTarget = 'overview' | 'cover'

export interface ArticleComment {
  id: string
  section: string
  slug: string
  target: ArticleCommentTarget
  /** One frame the comment is about (an image id), or null for the whole page. */
  targetRef: string | null
  body: string
  author: string | null
  resolvedAt: string | null
  resolvedBy: string | null
  createdAt: string
}

export interface InsightsSpend {
  cap: CoverCap
  covers: { totalUsd: number; byKind: { kind: string; runs: number; usd: number }[] }
  /** What the site's audio cost at the listed rate; null when the manifest cannot be read. */
  narration: { pieces: number; chars: number; minutes: number; usd: number } | null
}

export interface TestsRequest {
  conceptSetId: string
  conceptId: string
  edited?: Partial<Pick<CoverConcept, 'metaphor' | 'alt' | 'subject_override' | 'tag_visibility'>>
  customPrompt?: string | null
}

/** The backend's reason, whatever shape Nest gave it (string, string[], or {message, problems}). */
const reason = (e: any, what: string): Error => {
  const m = e?.response?.data?.message ?? e?.message
  const err = new Error(Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : `Failed to ${what}`) as Error & { status?: number; refused?: boolean }
  err.status = e?.response?.status ?? e?.status
  err.refused = !!e?.response?.data?.refused
  return err
}

async function call<T>(method: 'GET' | 'POST' | 'PUT', url: string, what: string, data?: unknown): Promise<T> {
  try {
    const r = await createApiCall({ method, url, data: data ?? {} })
    return r.data as T
  } catch (e) {
    throw reason(e, what)
  }
}

const base = (section: string, slug: string) => `/internal/gtm/covers/articles/${encodeURIComponent(section)}/${encodeURIComponent(slug)}`

export const coversApi = {
  state: (section: string, slug: string) => call<CoverState>('GET', base(section, slug), 'load the cover state'),
  /** Every locked cover in the tool's mode (the Articles list's Next step column). */
  approvals: () => call<{ approvals: CoverApproval[] }>('GET', '/internal/gtm/covers/approvals', 'load the locked covers'),
  runs: (limit = 100) => call<{ cap: CoverCap; runs: CoverRun[] }>('GET', `/internal/gtm/covers/runs?limit=${limit}`, 'load the run log'),
  setCap: (monthlyCapUsd: number) => call<CoverCap>('PUT', '/internal/gtm/covers/settings', 'save the cap', { monthlyCapUsd }),
  generateConcepts: (section: string, slug: string, body: { articleText?: string; title?: string }) =>
    call<{ conceptSetId: string; valid: boolean; problems: string[] }>('POST', `${base(section, slug)}/concepts`, 'generate concepts', body),
  generateTests: (section: string, slug: string, body: TestsRequest) =>
    call<{ runId: string }>('POST', `${base(section, slug)}/tests`, 'generate tests', body),
  /** rules v2 trial flow: ONE image of EACH concept in the set, each with QA. */
  generateSample: (section: string, slug: string, body: { conceptSetId: string }) =>
    call<{ runId: string }>('POST', `${base(section, slug)}/sample`, 'generate one image of each concept', body),
  approve: (section: string, slug: string, body: { imageId: string; alt?: string; title?: string; upscale?: boolean }) =>
    call<CoverApproval>('POST', `${base(section, slug)}/approve`, 'approve the cover', body),
  retryPr: (section: string, slug: string, title?: string) => call<CoverApproval>('POST', `${base(section, slug)}/pr`, 'open the website PR', { title }),
  /** Image bytes through the authenticated API (tests are never public URLs). */
  imageBlob: async (url: string): Promise<Blob> => {
    try {
      const r = await createApiCall({ method: 'GET', url, responseType: 'blob' })
      return r.data as Blob
    } catch (e) {
      throw reason(e, 'load the image')
    }
  },
  imageUrl: (id: string) => `/internal/gtm/covers/images/${id}`,
  spend: () => call<InsightsSpend>('GET', '/internal/gtm/covers/spend', 'load the spend'),
  comments: (section: string, slug: string) => call<{ comments: ArticleComment[] }>('GET', `${base(section, slug)}/comments`, 'load the comments'),
  addComment: (section: string, slug: string, body: { target: ArticleCommentTarget; targetRef: string | null; body: string }) =>
    call<ArticleComment>('POST', `${base(section, slug)}/comments`, 'save the comment', body),
  resolveComment: (section: string, slug: string, id: string) => call<ArticleComment>('POST', `${base(section, slug)}/comments/${encodeURIComponent(id)}/resolve`, 'resolve the comment'),
  approvedFileUrl: (section: string, slug: string, which: 'webp' | 'master') => `${base(section, slug)}/files/${which}`,
}

// ── VRG-733 — the website's articles, read fresh (`/internal/gtm/article-content/*`) ──────────
// Canonical shapes: `apps/api/src/features/gtm/covers/article-content.service.ts` and
// `website-article-content.ts`. Keep in step.

export type WebsiteCover = { type: 'image'; src: string; alt: string } | { type: 'motif'; motif: string | null } | { type: 'none' }

export interface WebsiteArticle {
  section: 'insights' | 'learn'
  slug: string
  path: string
  /** Where the website serves it now (explainers moved to /insights/explainers/<slug>, 2026-10-22). */
  href?: string
  kind: string
  type?: string
  format?: string
  title: string
  description: string
  cover: WebsiteCover
  wordCount: number
  readMinutes: number | null
  publishedAt: string
  updatedAt: string | null
  reviewedAt?: string | null
  sourceCount: number
  status: 'published' | 'staged'
  contentHash: string
}

export interface WebsiteManifestView {
  available: boolean
  reason: string | null
  schemaVersion: number | null
  sections: Record<string, { path: string; href?: string; status: string }>
  articles: WebsiteArticle[]
  source: { ref: string; label: string; readAt: string; mode: CoverMode } | null
}

export interface ContentBlock {
  kind: 'heading' | 'paragraph' | 'list' | 'cards' | 'facts' | 'timeline' | 'status' | 'figure' | 'other'
  text?: string
  eyebrow?: string
  /** A figure's caption (finding · scope · source) and alt text, as the site prints them. */
  caption?: string
  alt?: string
  items?: string[]
  pairs?: { label: string; value: string; status?: string }[]
  /** Footnote numbers (1-based positions in the article's sources, as the site numbers them). */
  cites?: number[]
}

export interface ArticleContent {
  file: string
  type: string | null
  format: string | null
  title: string | null
  lead: string | null
  description: string | null
  cardSummary: string | null
  solutions: string[] | null
  industries: string[] | null
  keywords: string[] | null
  body: ContentBlock[] | null
  bodyNote: string | null
  sources: number | null
  citedSources: number | null
}

export interface ArticleDetail {
  article: WebsiteArticle | null
  content: ArticleContent | null
  contentNote: string | null
  coverFile: string | null
  source: WebsiteManifestView['source']
  manifestReason: string | null
}

const contentBase = (section: string, slug: string) => `/internal/gtm/article-content/articles/${encodeURIComponent(section)}/${encodeURIComponent(slug)}`

export const articleContentApi = {
  manifest: () => call<WebsiteManifestView>('GET', '/internal/gtm/article-content/manifest', "read the website's article manifest"),
  detail: (section: string, slug: string) => call<ArticleDetail>('GET', contentBase(section, slug), 'load the article'),
  /** Through the authenticated API (staged covers are not public on vera.inc). */
  coverUrl: (section: string, slug: string, width?: number) => `${contentBase(section, slug)}/cover${width ? `?w=${width}` : ''}`,
}
